import { GridRow, GridRowId, GridRowModel, GridRowProps, useGridApiContext } from '@mui/x-data-grid-premium'
import Link from 'next/link'
import { createContext, useContext } from 'react'
import {
  dataGridGroupingRowVariant,
  dataGridRowVariantAttribute,
  type OmneaDataGridRowVariant,
} from '@omnea-app/omui/dataGridClasses'
import { useEditingRowsContext } from './RowEditingContext'

export interface RowVariantProp {
  // Method syntax is deliberate: it makes `row` bivariant, so a consumer can type its resolver with its own
  // row type rather than the grid's loose GridRowModel. Must also be a stable reference — it reaches every
  // row via slotProps, so a new identity each render re-renders the whole grid. Hoist it to module scope,
  // or useCallback if it closes over state.
  getRowVariant?(row: GridRowModel): OmneaDataGridRowVariant | undefined
}

// MUI's extension point for props reaching the row slot, which is how DataGridBase forwards getRowVariant.
declare module '@mui/x-data-grid' {
  interface RowPropsOverrides {
    getRowVariant?: RowVariantProp['getRowVariant']
  }
}

export const RowLinkContext = createContext<((id: GridRowId) => string | null) | undefined>(undefined)

export const LinkableGridRow = ({ getRowVariant, ...rowProps }: GridRowProps & RowVariantProp) => {
  const apiRef = useGridApiContext()
  const { currentlyEditingRow } = useEditingRowsContext()
  const getRowLink = useContext(RowLinkContext)

  // Group rows expand on click; never wrap them in a row link. Server-grouping group rows carry
  // a `group` node type rather than MUI's `auto-generated-row` id, so check both.
  const isGroupRow =
    Boolean(String(rowProps.rowId).match(/^auto-generated-row/)) || apiRef.current?.getRowNode(rowProps.rowId)?.type === 'group'

  const variant = isGroupRow ? dataGridGroupingRowVariant : getRowVariant?.(rowProps.row)
  const gridRow = <GridRow {...rowProps} {...(variant ? { [dataGridRowVariantAttribute]: variant } : {})} />

  if (isGroupRow) return gridRow

  const rowLink = getRowLink?.(rowProps.rowId)

  if (typeof rowLink !== 'string' || currentlyEditingRow === rowProps.rowId.toString()) {
    return gridRow
  }

  return <Link href={rowLink}>{gridRow}</Link>
}
